import { Header, Menu, Popup } from '@mairistem/composants';
import {
	formatMontant,
	Icon,
	UserRole,
	useUserRole,
} from '@mairistem/finances-utils';
import { ListeTable } from '@mairistem/liste';
import React from 'react';
import type Projet from '../../interfaces/common/projet';
import { renderLabel } from '../../utils/autorisation';
import AutorisationDeleteModal from './AutorisationDeleteModal';
import AutorisationModal from './AutorisationModal';
import './AutorisationListRow.css';

interface AutorisationProgrameListRowProps {
	autorisation: Projet;
	decision: number;
	onDelete: (identifiant: Projet['identifiant']) => void;
	onFinish: (data: Projet) => void;
}

const AutorisationListRow = ({
	autorisation,
	decision,
	onDelete,
	onFinish,
}: AutorisationProgrameListRowProps) => {
	const [role] = useUserRole();
	const isSuperUser = role === UserRole.ADMIN;
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
	const [isPopupOpen, setIsPopupOpen] = React.useState(false);

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleCloseDeleteModal = () => {
		setIsDeleteModalOpen(false);
	};

	const handleClosePopup = () => {
		setIsPopupOpen(false);
	};

	const handleFinishModal = (data: Projet) => {
		onFinish(data);
		handleCloseModal();
	};

	const handleDeleted = () => {
		onDelete(autorisation.identifiant);
	};

	const handleOpenModal = () => {
		setIsPopupOpen(false);
		setIsModalOpen(true);
	};

	const handleOpenDeleteModal = () => {
		setIsPopupOpen(false);
		setIsDeleteModalOpen(true);
	};

	const handleOpenPopup = () => {
		setIsPopupOpen(true);
	};

	return (
		<>
			<ListeTable.Row key={autorisation.identifiant}>
				<ListeTable.Cell name="libelle">
					<Header size="small">
						<Header.Content>
							{autorisation.libelle}
							<Header.Subheader content={autorisation.code ?? ''} />
						</Header.Content>
					</Header>
				</ListeTable.Cell>
				<ListeTable.Cell name="categorie">
					{autorisation.etiquette?.libelle}
				</ListeTable.Cell>
				<ListeTable.Cell name="montantAutorisation" textAlign="right">
					{formatMontant(autorisation.montantAutorisation)}
				</ListeTable.Cell>
				<ListeTable.Cell name="dateVote">
					{autorisation.dateVote
						? new Intl.DateTimeFormat('fr-FR').format(
								new Date(autorisation.dateVote),
							)
						: 'Non délibérée'}
				</ListeTable.Cell>
				<ListeTable.Cell name="statut">
					{renderLabel(autorisation.statut)}
				</ListeTable.Cell>
				<ListeTable.Cell name="actions">
					<Popup
						content={
							<Menu borderless vertical size="small">
								<Menu.Item
									className="autorisationActionItem"
									onClick={handleOpenModal}
								>
									<Icon iconSet="Lucide" name="Eye" size={16} />
									Consulter / Modifier
								</Menu.Item>
								{isSuperUser && (
									<Menu.Item
										className="autorisationActionItem"
										onClick={handleOpenDeleteModal}
									>
										<Icon iconSet="Lucide" name="Trash" size={16} />
										Supprimer
										<Icon color='orange' iconSet="Lucide" name="Crown" size={16} />
									</Menu.Item>
								)}
							</Menu>
						}
						on="click"
						onClose={handleClosePopup}
						onOpen={handleOpenPopup}
						open={isPopupOpen}
						size="tiny"
						trigger={
							<Icon
								data-testid={`autorisationActionMenu${autorisation.identifiant}`}
								iconSet="Lucide"
								name="Ellipsis"
							/>
						}
					/>
				</ListeTable.Cell>
			</ListeTable.Row>
			{isModalOpen && (
				<AutorisationModal
					decision={decision}
					identifiantAutorisation={autorisation.identifiant as number}
					onClose={handleCloseModal}
					onFinish={handleFinishModal}
				/>
			)}
			{isDeleteModalOpen && (
				<AutorisationDeleteModal
					autorisation={autorisation}
					onClose={handleCloseDeleteModal}
					onDeleted={handleDeleted}
				/>
			)}
		</>
	);
};

export default AutorisationListRow;
